import React from "react";
import { Box, Grid, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../../utils/formModalStyles";
import { createSupplier } from "../../../services/supplierService";
import {
  handleFormattedGstBlur,
  handleFormattedNameBlur,
} from "../../../utils/nameFormatUtils";
import { useSelector } from "react-redux";
import { getOrganizationId } from "../../../utils/organizationUtils";

const validationSchema = Yup.object({
  name: Yup.string().required("Supplier Name is required"),
  contact: Yup.string()
    .required("Supplier Contact is required")
    .test("is-10-digits", "Supplier Contact must be exactly 10 digits", function (value) {
      if (!value) return true;
      const digitsOnly = value.replace(/\D/g, "");
      return digitsOnly.length === 10;
    }),
  address: Yup.string().required("Supplier Address is required"),
  gstNumber: Yup.string().required("GST Number is required"),
});

const AddSupplierModal = ({ open, onClose, onCreated }) => {
  const user = useSelector((state) => state.user.user);

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      contact: "",
      gstNumber: "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const organizationId = getOrganizationId(user);
        const payload = {
          name: values.name.trim(),
          address: values.address.trim(),
          contact: values.contact.trim(),
          gstNumber: values.gstNumber.trim(),
        };

        if (organizationId && organizationId > 0) {
          payload.organizationId = organizationId;
        }

        await createSupplier(payload);
        alert("Supplier added successfully!");
        onCreated?.();
        handleClose();
      } catch (error) {
        console.error("Error creating supplier:", error);
        alert(error?.message || "Failed to add supplier. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <BasicModal open={open} onClose={handleClose} title="Add Supplier">
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Supplier Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleFormattedNameBlur(e, {
                    setFieldValue: formik.setFieldValue,
                    handleBlur: formik.handleBlur,
                  })
                }
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="contact"
                name="contact"
                label="Contact"
                value={formik.values.contact}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  formik.setFieldValue("contact", value);
                }}
                onBlur={formik.handleBlur}
                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                error={formik.touched.contact && Boolean(formik.errors.contact)}
                helperText={formik.touched.contact && formik.errors.contact}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="gstNumber"
                name="gstNumber"
                label="GST Number"
                value={formik.values.gstNumber}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleFormattedGstBlur(e, {
                    setFieldValue: formik.setFieldValue,
                    handleBlur: formik.handleBlur,
                  })
                }
                error={formik.touched.gstNumber && Boolean(formik.errors.gstNumber)}
                helperText={formik.touched.gstNumber && formik.errors.gstNumber}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={(e) =>
                  handleFormattedNameBlur(e, {
                    setFieldValue: formik.setFieldValue,
                    handleBlur: formik.handleBlur,
                  })
                }
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <FormModalActions
                onCancel={handleClose}
                submitType="submit"
                submitLabel={formik.isSubmitting ? "Saving..." : "Save"}
                submitDisabled={formik.isSubmitting}
                loading={formik.isSubmitting}
              />
            </Grid>
          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default AddSupplierModal;
